/**
 * Branch Store - Multi-branch context management
 *
 * Manages branch selection and multi-branch detection for the application.
 * Single-branch users see no branch UI; multi-branch users get a selector.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface BranchAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  latitude?: number;
  longitude?: number;
}

export interface BranchLocation {
  id: string;
  name: string;
  address: BranchAddress;
  description?: string;
  status: 'open' | 'closed';
  openDate?: string;
  closeDate?: string;
}

export interface Branch {
  id: string;
  name: string;
  code: string;
  address: BranchAddress | null;
  timezone: string;
  phone: string | null;
  email: string | null;
  color: string | null;
  isActive: boolean;
  isDefault: boolean;
  locations: BranchLocation[];
  createdAt?: string;
  updatedAt?: string;
}

interface BranchState {
  // All active branches in the system
  branches: Branch[];
  // User's assigned branches
  userBranches: Branch[];
  // Currently selected branch (null = all branches view)
  currentBranchId: string | null;
  // Whether user has multi-branch access
  isMultiBranchUser: boolean;
  // Whether the system has multiple branches enabled
  isMultiBranchEnabled: boolean;
  // Loading/initialized state
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  setCurrentBranch: (branchId: string | null) => void;
  fetchUserBranches: (token: string) => Promise<void>;
  reset: () => void;
  getCurrentBranchForQuery: () => string;
}

const initialState = {
  branches: [],
  userBranches: [],
  currentBranchId: null,
  isMultiBranchUser: false,
  isMultiBranchEnabled: false,
  isLoading: false,
  isInitialized: false,
  error: null,
};

export const useBranchStore = create<BranchState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setCurrentBranch: (branchId: string | null) => {
        const { userBranches, isMultiBranchUser } = get();

        // Single-branch users can't change branch
        if (!isMultiBranchUser) {
          return;
        }

        // Validate branch is in user's allowed list
        if (branchId !== null && !userBranches.some(b => b.id === branchId)) {
          console.warn('User does not have access to branch:', branchId);
          return;
        }

        set({ currentBranchId: branchId });
      },

      fetchUserBranches: async (token: string) => {
        set({ isLoading: true, error: null });

        // Retry logic for transient network failures
        const maxRetries = 3;
        let lastError: Error | null = null;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            const response = await fetch(`${API_URL}/api/v1/branches/me`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });

            if (!response.ok) {
              const errorText = await response.text().catch(() => 'Unknown error');
              throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const result = await response.json();

            if (!result.success) {
              throw new Error(result.error?.message || 'Failed to fetch branch info');
            }

            const data = result.data as {
              assignedBranches: Branch[];
              defaultBranch: Branch | null;
              currentBranchId: string | null;
              isMultiBranchUser: boolean;
              isMultiBranchEnabled: boolean;
            };

            // Get current state
            const currentState = get();

            // Determine current branch ID
            let newCurrentBranchId = currentState.currentBranchId;

            // If not already set, use the API-provided value or default
            if (newCurrentBranchId === null) {
              newCurrentBranchId = data.currentBranchId || data.defaultBranch?.id || null;
            }

            // Validate it's still in allowed branches
            if (newCurrentBranchId && !data.assignedBranches.some(b => b.id === newCurrentBranchId)) {
              newCurrentBranchId = data.defaultBranch?.id || data.assignedBranches[0]?.id || null;
            }

            set({
              branches: data.assignedBranches,
              userBranches: data.assignedBranches,
              currentBranchId: newCurrentBranchId,
              isMultiBranchUser: data.isMultiBranchUser,
              isMultiBranchEnabled: data.isMultiBranchEnabled,
              isLoading: false,
              isInitialized: true,
              error: null,
            });
            return; // Success - exit retry loop
          } catch (error) {
            lastError = error instanceof Error ? error : new Error('Unknown error');
            console.warn(`Branch fetch attempt ${attempt + 1}/${maxRetries} failed:`, lastError.message);

            // If this wasn't the last attempt, wait before retrying (exponential backoff)
            if (attempt < maxRetries - 1) {
              const delayMs = Math.min(1000 * Math.pow(2, attempt), 5000); // Max 5s delay
              await new Promise(resolve => setTimeout(resolve, delayMs));
            }
          }
        }

        // All retries failed
        console.error('Failed to fetch branch info after retries:', lastError);
        set({
          isLoading: false,
          isInitialized: true,
          error: lastError?.message || 'Failed to fetch branch info',
        });
      },

      reset: () => {
        set(initialState);
      },

      /**
       * Get the branch ID to use in API queries.
       * Returns 'all' for multi-branch users viewing all branches,
       * or the specific branch ID otherwise.
       */
      getCurrentBranchForQuery: () => {
        const { currentBranchId, isMultiBranchUser, userBranches } = get();

        // Single-branch users: always use their one branch
        if (!isMultiBranchUser && userBranches.length > 0) {
          return userBranches[0].id;
        }

        // Multi-branch users: null means "all", otherwise specific branch
        return currentBranchId || 'all';
      },
    }),
    {
      name: 'twg-branch-store',
      // Only persist currentBranchId - rest comes from API
      partialize: (state) => ({
        currentBranchId: state.currentBranchId,
      }),
    }
  )
);

/**
 * Hook to get the current branch for display purposes.
 * Returns null if viewing all branches or if single-branch user.
 */
export function useCurrentBranch(): Branch | null {
  const { currentBranchId, userBranches, isMultiBranchUser } = useBranchStore();

  if (!isMultiBranchUser || !currentBranchId) {
    return null;
  }

  return userBranches.find(b => b.id === currentBranchId) || null;
}

/**
 * Hook to check if branch selector should be visible.
 * Only visible when both system has multiple branches AND user has multi-branch access.
 */
export function useShouldShowBranchSelector(): boolean {
  const { isMultiBranchEnabled, isMultiBranchUser } = useBranchStore();
  return isMultiBranchEnabled && isMultiBranchUser;
}
